/**
 * CASEWORK ROWS (docs/bim-plan/36-casework-rows.md, stage 1 — 2026-10-21): a casework run is a
 * stack of ROWS across its length. Row 0 stands on the floor (bases, talls); row 1 is today's
 * upper layer; rows above it are stacked or hung higher. A box starts in one row (`runRow`) and
 * covers `rowSpan` rows — a tall box is a row-0 box with a span over 1. A row's BAYS are the
 * stretches no lower box reaches into.
 *
 * Stage 1 writes nothing new: when `runRow` / `rowSpan` are absent they are DERIVED from the box's
 * style exactly as the old floor/upper layers were (upper → row 1; tall → row 0 reaching through
 * row 1; everything else → row 0), so every existing model lays out as before.
 *
 * Pure: no doc access, so doc.ts and commands/caseworkRun.ts can both use it without a cycle.
 */

/** where a row's bottom sits */
export type RowBottom = 'floor' | 'counter' | 'stacked' | 'hung'

export interface RunRowSetting {
  bottom: RowBottom
  /** for 'hung': the bottom's height above the floor */
  bottomFt?: number | null
  heightFt?: number | null
  depthFt?: number | null
  /** stage 3 (Matt, Q6): a counter-standing row may wear a low base molding where it meets the
   *  counter, so it reads as furniture set on top (plain by default) */
  baseMolding?: boolean
}

/** a tall box (no stored span) reaches through the base row and today's upper row */
export const TALL_DEFAULT_SPAN = 2

const isInt = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v)

/** a box's row and span: its own stored `runRow` / `rowSpan`, else derived from its style */
export function rowOfParams(params: Record<string, unknown>, style: string): { row: number; span: number } {
  const derivedRow = style === 'upper' ? 1 : 0
  const row = isInt(params.runRow) && params.runRow >= 0 ? params.runRow : derivedRow
  const derivedSpan = row === 0 && style === 'tall' ? TALL_DEFAULT_SPAN : 1
  const span = isInt(params.rowSpan) && params.rowSpan >= 1 ? params.rowSpan : derivedSpan
  return { row, span }
}

/**
 * The key a row is filed under (corners, layout): row 0 is the run itself, row 1 keeps today's
 * `<runId>#upper` so stored corner choices and caches never change, row n ≥ 2 is `<runId>#row<n>`.
 */
export function rowKey(runId: string, row: number): string {
  return row <= 0 ? runId : row === 1 ? `${runId}#upper` : `${runId}#row${row}`
}

/** the run id and row behind a rowKey */
export function parseRowKey(key: string): { runId: string; row: number } {
  if (key.endsWith('#upper')) return { runId: key.slice(0, -6), row: 1 }
  const m = /^(.*)#row(\d+)$/.exec(key)
  if (m) return { runId: m[1], row: Number(m[2]) }
  return { runId: key, row: 0 }
}

/**
 * A row's BAYS: the stretches of the run [0, lengthFt] that no box from a LOWER row reaches
 * into (a box in row r with span n covers rows r … r+n-1). `boxes` are the laid-out boxes of the
 * rows below, each with its stretch along the run.
 */
export function rowBays(
  boxes: { s0: number; s1: number; row: number; span: number }[],
  lengthFt: number,
  row: number,
): [number, number][] {
  const covered = boxes
    .filter((b) => b.row < row && b.row + b.span > row && b.s1 - b.s0 > 1e-9)
    .map((b) => [Math.max(0, b.s0), Math.min(lengthFt, b.s1)] as [number, number])
    .sort((a, b) => a[0] - b[0])
  const out: [number, number][] = []
  let open = 0
  for (const [a, b] of covered) {
    if (a - open > 1e-6) out.push([open, a])
    open = Math.max(open, b)
  }
  if (lengthFt - open > 1e-6) out.push([open, lengthFt])
  return out
}

/**
 * The run's rows as settings, bottom to top: the run's own stored `runRows` where it has them,
 * else derived — row 0 on the floor at the base's height, and (when the run has row-1 boxes) row
 * 1 hung at the upper type's mount height, height and depth. Stage 1 reads only; nothing writes
 * `runRows` yet.
 */
export function rowsFromParams(
  stored: unknown,
  base: { heightFt?: number; depthFt?: number },
  upper: { mountHeightFt?: number; heightFt?: number; depthFt?: number } | null,
  rowCount: number,
): RunRowSetting[] {
  const own = Array.isArray(stored) ? (stored as RunRowSetting[]) : []
  const derived: RunRowSetting[] = [{ bottom: 'floor', bottomFt: 0, heightFt: base.heightFt ?? null, depthFt: base.depthFt ?? null }]
  if (rowCount > 1) {
    derived.push({ bottom: 'hung', bottomFt: upper?.mountHeightFt ?? null, heightFt: upper?.heightFt ?? null, depthFt: upper?.depthFt ?? null })
  }
  for (let r = 2; r < rowCount; r++) derived.push({ bottom: 'stacked' })
  return derived.map((d, r) => (own[r] && typeof own[r] === 'object' ? { ...d, ...own[r] } : d))
}

/** a new stacked row with no ceiling to reach starts this tall (Matt, Q2: 15") */
export const ROW_FALLBACK_HEIGHT_FT = 15 / 12
/** a ceiling gap under this starts the row at the fallback instead */
export const ROW_MIN_CEILING_GAP_FT = 6 / 12
/** the countertop a counter-standing row sits on, when the run's own thickness is unknown (1-1/2") */
export const ROW_COUNTER_FT = 1.5 / 12
/** a counter-standing row's base molding height (stage 3, Q6) */
export const ROW_BASE_MOLDING_IN = 3

/**
 * Stage 2: where each row's bottom and top fall above the floor. Row 0 stands on the floor; a
 * hung row sits at its own height; a stacked row sits on the top of the row below; a
 * counter-standing row on the row-0 counter.
 */
export function rowLevels(rows: RunRowSetting[], counterFt = ROW_COUNTER_FT): { bottoms: number[]; tops: number[] } {
  const bottoms: number[] = [], tops: number[] = []
  rows.forEach((r, i) => {
    const h = typeof r.heightFt === 'number' && r.heightFt > 0 ? r.heightFt : i === 0 ? 3 : ROW_FALLBACK_HEIGHT_FT
    const b = i === 0 ? 0
      : r.bottom === 'hung' ? (typeof r.bottomFt === 'number' ? r.bottomFt : 4.5)
        : r.bottom === 'counter' ? tops[0] + counterFt
          : tops[i - 1]
    bottoms.push(b)
    tops.push(b + h)
  })
  return { bottoms, tops }
}
